/**
 * The locked-campaign explainer — banner + modal (PREC-3786). Its own file because en.ts is at the
 * size ratchet and the `campaign.builder.status` block this would otherwise sit in is shared.
 *
 * Every claim below was checked against the code that enforces the lock, because the wording it
 * replaces was not true any more:
 * - the lock follows `hasProcessedStep` (PREC-3853) — a step was actually sent, not "leads were
 *   assigned", which stopped locking anything;
 * - what it blocks is adding/deleting steps, condition branches, senders and a step's channel;
 * - a duplicate is created without senders, so it is invalid until the user picks them;
 * - campaign stats group by the lead's *current* campaign, so moving a lead moves its results.
 */
export const enCampaignLockExplainer = {
	bannerTitle: "This campaign is locked",
	bannerText:
		"A step of this sequence has already been sent, so the step structure is fixed. You can still edit message text.",
	why: "Why?",
	title: "This campaign is locked",
	subtitle:
		"Locked because at least one step of the sequence has already been sent to a lead. The lock stays in place even if those leads are removed later.",
	restrictionsHeading: "What you can and can't change",
	rules: {
		structure: {
			title: "Add, remove or reorder steps and conditions",
			text: "The sequence structure is fixed: no new steps, no deleting a step, and no changing which branch a condition takes.",
		},
		senders: {
			title: "Change senders or a step's channel",
			text: "Which mailboxes send, and whether a step is an email or a LinkedIn action, are fixed for every step.",
		},
		messageText: {
			title: "Edit message text",
			text: "Subject lines, message bodies and signatures stay editable. Edits only affect messages that haven't been sent yet.",
		},
	},
	howToHeading: "How can I change the campaign for the assigned leads?",
	howToText:
		"Duplicate this campaign, make your edits, then assign leads to the copy. The duplicate is created without senders, so pick them before you activate it. Keep in mind that the leads sit at different points in the sequence:",
	leadStates: {
		scheduled: {
			name: "Scheduled leads",
			count_one: "{{count}} lead in this campaign",
			count_other: "{{count}} leads in this campaign",
			verdict: "Safe to re-assign",
			text: "Nothing has been sent to them yet, so they start clean in the new campaign.",
		},
		running: {
			name: "Running leads",
			count_one: "{{count}} lead has received at least the first step",
			count_other: "{{count}} leads have received at least the first step",
			verdict: "Be cautious!",
			text: "Running leads are spread across different steps, so they can't all be moved into one new campaign that starts from the beginning.",
		},
		ended: {
			name: "Ended leads",
			count_one: "{{count}} lead has ended in this campaign",
			count_other: "{{count}} leads have ended in this campaign",
			verdict: "Safe to re-assign",
			text: "Every step has run and the campaign has ended for them, so moving them interrupts nothing.",
		},
	},
	analyticsWarningTitle: "Moving a lead moves its results with it",
	analyticsWarning:
		"Contacted, reply and positive-reply counts always follow the campaign a lead is assigned to right now — not the one that earned them. “{{name}}” loses the results of every lead you move, and the copy gains them. Our recommendation: move only the scheduled leads and let the rest finish where they are.",
	footerHint:
		"Duplicating copies all steps and message text. Senders are not copied and no leads are moved.",
	close: "Close",
	duplicate: "Duplicate campaign",
	duplicateSuccess: "Duplicated as “{{name}}”. Pick its senders to activate it.",
	duplicateError: "The campaign could not be duplicated. Please try again.",
};
